/**
 * Unified Slack flow configuration supporting multiple flow scenarios
 * Handles: basic, enhanced (with tools), block, and warn/proceed flows
 */

import {
  AlertTriangle,
  ShieldCheck,
  Route,
  User as UserIcon,
  Sparkles,
  MousePointerClick,
  XCircle,
} from 'lucide-react';
import { ObservabilityLogsRetrieve200ResultsItem } from '@/gen-api/models';
import { FlowDefinition, DialogField, FlowNodeConfig, EdgeConfig } from '../types';
import { SlackLogo } from '../ui/SlackLogo';
import { ToolCallDisplay, ToolCallDetail } from '../ui/ToolCallDisplay';
import { AttachmentList, AttachmentResult } from '../ui/AttachmentCard';
import { FileAttachmentsList } from '../ui/FileAttachmentsList';
import { AttachmentComplianceButton } from '../ui/AttachmentComplianceButton';
import {
  deriveAssistantText,
  deriveMessageText,
  deriveUserString,
  pickFirstNumberOrString,
  pickFirstString,
  unwrap,
  getByPath,
  MetaDoc,
} from '../utils/metadata';
import { formatTimestamp, statusColor, toDisplay } from '../utils/formatting';
import ReactMarkdown from 'react-markdown';

type FlowType = 'basic' | 'enhanced' | 'block' | 'warn-proceed';

interface FlowConfig {
  type: FlowType;
}

/**
 * Helper: Format a Unix epoch float string (e.g. "1744567890.123456") to a locale string
 */
const formatUnixTs = (value: unknown): string | undefined => {
  const n = parseFloat(String(value));
  if (isNaN(n)) return undefined;
  return new Date(n * 1000).toLocaleString();
};

/**
 * Helper: Determine the worst action between text prompt and attachments
 * Hierarchy: BLOCK > AMEND > WARN > ALLOW
 */
const getWorstAction = (md: MetaDoc): string | undefined => {
  const textAction = (md.filter_response as { value?: { action?: string } })?.value?.action;
  const attachmentsAction = (md.attachments_action as { value?: string })?.value || (md.attachments_action as string);

  const actions = [textAction, attachmentsAction].filter(Boolean);

  if (actions.includes('BLOCK')) return 'BLOCK';
  if (actions.includes('AMEND')) return 'AMEND';
  if (actions.includes('WARN')) return 'WARN';
  if (actions.includes('ALLOW')) return 'ALLOW';

  return textAction; // Fallback to text action
};

/**
 * Helper: Build user message node
 */
const buildUserMessageNode = (xPos = 0, yPos = 0, subtitle = 'Inbound message from Slack'): FlowNodeConfig => ({
  id: 'n1',
  title: 'Slack User Message',
  subtitle,
  icon: <SlackLogo size={18} />,
  xPosition: xPos,
  yPosition: yPos,
  dialogBuilder: (item: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) => {
    const itemTimestamp = (item.created_at || item.logs?.[0]?.created_at) as string | undefined;
    const fields: DialogField[] = [];

    const user = deriveUserString(item, md);
    if (user) fields.push({ label: 'User', value: toDisplay(user) });

    const channel = pickFirstString(md, ['slack_data.slack_channel_name', 'channel_name', 'channel']);
    if (channel) fields.push({ label: 'Channel', value: toDisplay(channel) });

    const message =
      deriveMessageText(md) ??
      pickFirstString(md, [
        'original_message_text',
        'original_user_message',
        'slack_data.query',
        'query',
        'message',
        'user_message_text',
        'text',
        'prompt',
      ]);
    if (message)
      fields.push({
        label: 'Message',
        value: (
          <div
            className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
            title={message}
          >
            <ReactMarkdown>{message}</ReactMarkdown>
          </div>
        ),
      });

    const msgReceivedAt = formatUnixTs(getByPath(md, 'message_received_at'));
    const processingStartedAt = formatUnixTs(getByPath(md, 'processing_started_at'));

    if (itemTimestamp && !msgReceivedAt && !processingStartedAt)
      fields.push({ label: 'Timestamp', value: formatTimestamp(itemTimestamp) });

    if (msgReceivedAt) fields.push({ label: 'Message Received At', value: msgReceivedAt });
    if (processingStartedAt) fields.push({ label: 'Processing Started At', value: processingStartedAt });

    const replySentAt = formatUnixTs(getByPath(md, 'reply_sent_at'));
    if (replySentAt) fields.push({ label: 'Reply Sent At', value: replySentAt });

    const uploadedFiles = getByPath(md, 'uploaded_files') as
      | Array<{ id: number; name: string; api_url?: string; content_type?: string }>
      | undefined;

    if (uploadedFiles && uploadedFiles.length > 0) {
      fields.push({
        label: `Attachments (${uploadedFiles.length})`,
        value: <FileAttachmentsList files={uploadedFiles} />,
        help: 'Files attached to the message. Click to download.',
      });
    }

    return fields;
  },
});

/**
 * Helper: Build compliance agent node
 */
const buildComplianceNode = (
  xPos = 250,
  yPos = 0,
  subtitle = 'Policy and safety checks',
  isBlockFlow = false
): FlowNodeConfig => ({
  id: 'n2',
  title: 'Compliance Agent',
  subtitle: isBlockFlow ? 'Message blocked' : subtitle,
  icon: <ShieldCheck size={18} color={isBlockFlow ? '#ef4444' : '#f59e0b'} />,
  xPosition: xPos,
  yPosition: yPos,
  dialogBuilder: (_item: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) => {
    const fields: DialogField[] = [];

    // Extract filter response data comprehensively
    const filterResponse = (
      md.filter_response as {
        value?: {
          action?: string;
          amendment?: string | null;
          reason?: string;
          resource_violations?: Record<string, unknown>;
          internal_monologue?: string;
        };
      }
    )?.value;

    if (isBlockFlow) {
      const filterAction = pickFirstString(md, ['overall_action_early', 'overall_action', 'action']);
      if (filterAction) {
        fields.push({
          label: 'Overall Action',
          value: <span className="font-semibold capitalize text-red-600">{filterAction.toLowerCase()}</span>,
          help: 'Final decision: Block (message rejected)',
        });
      }
    } else {
      const filterAction =
        filterResponse?.action || pickFirstString(md, ['overall_action_early', 'overall_action', 'action']);

      // Display with rich formatting (icons + colors)
      if (filterAction) {
        let actionColor = '';
        let actionIcon = '';
        let actionHelp = '';

        switch (filterAction) {
          case 'ALLOW':
            actionColor = 'text-green-600 dark:text-green-400';
            actionIcon = '✅';
            actionHelp = 'Message allowed - no violations detected';
            break;
          case 'WARN':
            actionColor = 'text-yellow-600 dark:text-yellow-400';
            actionIcon = '⚠️';
            actionHelp = 'Warning issued - user can proceed with original message';
            break;
          case 'AMEND':
            actionColor = 'text-orange-600 dark:text-orange-400';
            actionIcon = '✏️';
            actionHelp = 'Amendment suggested - user offered safer alternative';
            break;
          case 'BLOCK':
            actionColor = 'text-red-600 dark:text-red-400';
            actionIcon = '🚫';
            actionHelp = 'Message blocked - no further processing occurred';
            break;
        }

        fields.push({
          label: 'Text Message Compliance Action',
          value: (
            <span className={`font-semibold capitalize ${actionColor}`}>
              {actionIcon} {filterAction.toLowerCase()}
            </span>
          ),
          help: actionHelp,
        });
      }

      // Show amendment with markdown rendering
      if (filterAction === 'AMEND') {
        const amendment = filterResponse?.amendment;
        if (amendment) {
          fields.push({
            label: 'Suggested Amendment',
            value: (
              <div
                className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
                title={amendment}
              >
                <ReactMarkdown>{amendment}</ReactMarkdown>
              </div>
            ),
            help: 'Sanitized version offered to the user',
          });
        }
      }

      // Show internal monologue (LLM reasoning)
      const internalMonologue = filterResponse?.internal_monologue;
      if (internalMonologue) {
        fields.push({
          label: 'Internal Monologue',
          value: (
            <div
              className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
              title={internalMonologue}
            >
              <ReactMarkdown>{internalMonologue}</ReactMarkdown>
            </div>
          ),
          help: 'LLM reasoning process for this compliance decision',
        });
      }
    }

    // Show compliance reason with markdown rendering (for prompt only)
    const reason = filterResponse?.reason || pickFirstString(md, ['reason', 'detail', 'filter_response.reason']);
    if (reason) {
      fields.push({
        label: 'Compliance Reason',
        value: (
          <div
            className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
            title={reason}
          >
            <ReactMarkdown>{reason}</ReactMarkdown>
          </div>
        ),
        help: 'Why this action was triggered',
      });
    }

    // Show resource violations with detailed breakdown (for prompt only)
    const resourceViolations = filterResponse?.resource_violations;
    if (resourceViolations && Object.keys(resourceViolations).length > 0) {
      fields.push({
        label: 'Resource Violations',
        value: (
          <ul className="ml-4 list-disc">
            {Object.entries(resourceViolations).map(([ruleset, guards]) =>
              Array.isArray(guards) && guards.length > 0 ? (
                <li key={ruleset}>
                  <span className="font-semibold">{toDisplay(ruleset)}: </span>
                  {guards.map((g, i) => (
                    <span key={String(g)}>
                      {toDisplay(g)}
                      {i < guards.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </li>
              ) : (
                <li key={ruleset}>
                  <span className="font-semibold">{toDisplay(ruleset)}</span>
                </li>
              )
            )}
          </ul>
        ),
        help: 'Guardrails and policy rules triggered',
      });
    }

    // ========================================================================
    // ATTACHMENTS COMPLIANCE - Button that opens nested modal
    // ========================================================================
    const attachmentResults =
      (getByPath(md, 'attachment_results.value') as unknown[]) ||
      (getByPath(md, 'attachment_results') as unknown[]) ||
      unwrap<unknown[]>(md.attachment_results);

    if (attachmentResults && Array.isArray(attachmentResults) && attachmentResults.length > 0) {
      const attachmentsAction = (md.attachments_action as { value?: string })?.value;
      const amendedFileIds =
        (getByPath(md, 'amended_file_ids.value') as (string | number)[] | undefined) ||
        (getByPath(md, 'amended_file_ids') as (string | number)[] | undefined) ||
        [];

      fields.push({
        label: '', // Empty label - button is full-width
        value: (
          <AttachmentComplianceButton
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            attachmentResults={attachmentResults as any}
            overallAction={attachmentsAction}
            amendedFileIds={amendedFileIds}
          />
        ),
      });
    }

    return fields;
  },
});

/**
 * Helper: Build warning message node (for WARN/AMEND flows)
 */
const buildWarningNode = (xPos = 500, yPos = 0): FlowNodeConfig => ({
  id: 'n3',
  title: 'Slack Compliance Message',
  subtitle: 'Compliance message sent to user',
  icon: <AlertTriangle size={18} color="#f59e0b" />,
  xPosition: xPos,
  yPosition: yPos,
  dialogBuilder: (_item: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) => {
    const filterResponse = (
      md.filter_response as {
        value?: {
          action?: string;
          amendment?: string | null;
          reason?: string;
          resource_violations?: Record<string, unknown>;
          internal_monologue?: string;
        };
      }
    )?.value;

    const warningMsg = pickFirstString(md, ['warning_message_sent', 'early_amend_response.detail']);
    const filterAction = filterResponse?.action;
    const reason = filterResponse?.reason;
    const resourceViolations = filterResponse?.resource_violations;
    const internalMonologue = filterResponse?.internal_monologue;
    const amendment = filterResponse?.amendment;

    const fields: DialogField[] = [];

    // Show the compliance action with appropriate styling
    if (filterAction) {
      let actionColor = '';
      let actionIcon = '';
      let actionHelp = '';

      switch (filterAction) {
        case 'ALLOW':
          actionColor = 'text-green-600 dark:text-green-400';
          actionIcon = '✅';
          actionHelp = 'Message allowed - no violations detected';
          break;
        case 'WARN':
          actionColor = 'text-yellow-600 dark:text-yellow-400';
          actionIcon = '⚠️';
          actionHelp = 'Warning issued - user can proceed with original message';
          break;
        case 'AMEND':
          actionColor = 'text-orange-600 dark:text-orange-400';
          actionIcon = '✏️';
          actionHelp = 'Amendment suggested - user offered safer alternative';
          break;
        case 'BLOCK':
          actionColor = 'text-red-600 dark:text-red-400';
          actionIcon = '🚫';
          actionHelp = 'Message blocked - no further processing occurred';
          break;
      }

      fields.push({
        label: 'Compliance Action',
        value: (
          <span className={`font-semibold capitalize ${actionColor}`}>
            {actionIcon} {filterAction.toLowerCase()}
          </span>
        ),
        help: actionHelp,
      });
    }

    // Show the user-facing message if present
    if (warningMsg) {
      const label =
        filterAction === 'AMEND'
          ? 'Amendment Message'
          : filterAction === 'WARN'
            ? 'Warning Message'
            : filterAction === 'BLOCK'
              ? 'Block Message'
              : 'Compliance Message';

      fields.push({
        label,
        value: (
          <div
            className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
            title={warningMsg}
          >
            <ReactMarkdown>{warningMsg}</ReactMarkdown>
          </div>
        ),
        help: 'The formatted message displayed in Slack',
      });
    }

    // Show the amendment if AMEND action
    if (filterAction === 'AMEND' && amendment) {
      fields.push({
        label: 'Suggested Amendment',
        value: (
          <div
            className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
            title={amendment}
          >
            <ReactMarkdown>{amendment}</ReactMarkdown>
          </div>
        ),
        help: 'Sanitized version offered to the user',
      });
    }

    // Show compliance reason
    if (reason) {
      fields.push({
        label: 'Compliance Reason',
        value: (
          <div
            className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
            title={reason}
          >
            <ReactMarkdown>{reason}</ReactMarkdown>
          </div>
        ),
        help: 'Why this action was triggered',
      });
    }

    // Show resource violations and guardrails
    if (resourceViolations && Object.keys(resourceViolations).length > 0) {
      fields.push({
        label: 'Resource Violations',
        value: (
          <ul className="ml-4 list-disc">
            {Object.entries(resourceViolations).map(([ruleset, guards]) =>
              Array.isArray(guards) && guards.length > 0 ? (
                <li key={ruleset}>
                  <span className="font-semibold">{toDisplay(ruleset)}: </span>
                  {guards.map((g, i) => (
                    <span key={String(g)}>
                      {toDisplay(g)}
                      {i < guards.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </li>
              ) : (
                <li key={ruleset}>
                  <span className="font-semibold">{toDisplay(ruleset)}</span>
                </li>
              )
            )}
          </ul>
        ),
        help: 'Guardrails and policy rules triggered',
      });
    }

    // Show LLM reasoning
    if (internalMonologue) {
      fields.push({
        label: 'Internal Monologue',
        value: (
          <div
            className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
            title={internalMonologue}
          >
            <ReactMarkdown>{internalMonologue}</ReactMarkdown>
          </div>
        ),
        help: 'LLM reasoning: why this action was decided',
      });
    }

    return fields;
  },
});

/**
 * Helper: Build routing agent node
 */
const buildRoutingNode = (nodeId = 'n3', xPos = 600, yPos = 0): FlowNodeConfig => ({
  id: nodeId,
  title: 'Routing Agent',
  subtitle: 'Agent selection',
  icon: <Route size={18} color="#6366f1" />,
  xPosition: xPos,
  yPosition: yPos,
  dialogBuilder: (_item: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) => {
    const agentNameVal = getByPath(md, 'agent_name') as string | undefined;
    const agentVersionVal = getByPath(md, 'agent_version') as string | undefined;
    const systemPrompt = pickFirstString(md, ['system_prompt_content', 'safe_system_prompt']);

    const isRejection =
      systemPrompt &&
      (systemPrompt.includes('cannot process') ||
        systemPrompt.includes('cannot fulfill') ||
        systemPrompt.includes('sensitive details'));

    const fields: DialogField[] = [];

    // If no agent data is available, show informational message
    if (!agentNameVal && !systemPrompt && !isRejection) {
      fields.push({
        label: 'Status',
        value: '⚠️ Not applicable',
        help: 'The Slack bot was not routed for this message. Routing typically occurs when: (1) Message mentions the bot, (2) Bot is set to always-on mode, or (3) Message is a response in an active thread with the bot.',
      });
      return fields;
    }

    if (isRejection) {
      fields.push({
        label: 'Router Decision',
        value: '⛔ Rejected',
        help: 'Router rejected the request due to policy restrictions',
      });
    }

    const routerPath = unwrap<boolean>(md.router_question_path);
    if (routerPath !== undefined) {
      fields.push({
        label: 'Routing Agent Reply',
        value: toDisplay(routerPath),
        help: 'Whether the request was treated as a question',
      });
    }

    if (agentNameVal) fields.push({ label: 'Agent Name', value: toDisplay(agentNameVal) });
    if (agentVersionVal) fields.push({ label: 'Agent Version', value: toDisplay(agentVersionVal) });

    if (systemPrompt && nodeId !== 'n5') {
      fields.push({
        label: 'System Prompt',
        value: (
          <div
            className="prose prose-sm dark:prose-invert max-w-sm max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
            title={systemPrompt}
          >
            <ReactMarkdown>{systemPrompt}</ReactMarkdown>
          </div>
        ),
        help: 'System instructions for the agent',
      });
    }

    return fields;
  },
});

/**
 * Helper: Build agent execution node (with tool visualization)
 */
const buildAgentExecutionNode = (nodeId = 'n4', xPos = 750, yPos = 0): FlowNodeConfig => ({
  id: nodeId,
  title: 'Agent Execution',
  subtitle: 'LLM response generation',
  icon: <Sparkles size={18} color="#10b981" />,
  xPosition: xPos,
  yPosition: yPos,
  dialogBuilder: (_item: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) => {
    const agentNameRaw =
      (md.agent_name as { value?: string })?.value || (md.agent_name_for_response as { value?: string })?.value;

    let toolCallDetails = getByPath(md, 'tool_call_details.value') as ToolCallDetail[] | undefined;
    if (!toolCallDetails) {
      const directAccess = (md.tool_call_details as { value?: ToolCallDetail[] })?.value;
      if (Array.isArray(directAccess)) {
        toolCallDetails = directAccess;
      }
    }
    if (!toolCallDetails) {
      const unwrapped = unwrap(md.tool_call_details);
      if (Array.isArray(unwrapped)) {
        toolCallDetails = unwrapped;
      }
    }

    let usedTools: string[] = [];
    try {
      const rawUsedTools = unwrap(md.used_tools);
      if (Array.isArray(rawUsedTools)) {
        usedTools = rawUsedTools.filter((t): t is string => typeof t === 'string');
      }
    } catch {
      usedTools = [];
    }

    const fields: DialogField[] = [];

    // If no agent data is available, show informational message
    if (!agentNameRaw && toolCallDetails?.length === 0 && usedTools.length === 0) {
      fields.push({
        label: 'Status',
        value: '⚠️ Not applicable',
        help: 'Agent execution did not occur for this message. This typically means the Slack bot was not routed to execute due to message not mentioning the bot or other routing conditions.',
      });
      return fields;
    }

    if (agentNameRaw) fields.push({ label: 'Agent', value: toDisplay(agentNameRaw) });

    if (toolCallDetails && toolCallDetails.length > 0) {
      fields.push({
        label: 'Tools',
        value: <ToolCallDisplay toolCalls={toolCallDetails} />,
        help: 'Details about tool calls made during response generation',
      });
    } else if (usedTools.length > 0) {
      fields.push({
        label: 'Tools Used',
        value: toDisplay(usedTools.join(', ')),
        help: 'Tools invoked during response generation',
      });
    }

    const filesForChat = getByPath(md, 'files_for_chat.value') as (string | number)[] | undefined;
    if (filesForChat && filesForChat.length > 0) {
      fields.push({
        label: `Files Used (${filesForChat.length})`,
        value: toDisplay(filesForChat.map((f, idx) => `File ${idx + 1}: ${f}`).join(', ')),
        help: 'Files attached to context for the LLM',
      });
    }

    const responseText =
      deriveAssistantText(md) ??
      pickFirstString(md, ['response_text', 'clean_text', 'text', 'assistant_response', 'answer']);

    if (responseText) {
      fields.push({
        label: 'Response Preview',
        value: (
          <div
            className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
            title={responseText}
          >
            <ReactMarkdown>{responseText}</ReactMarkdown>
          </div>
        ),
        help: 'Response text rendered as markdown',
      });
    }

    return fields;
  },
});

/**
 * Helper: Build response node
 */
const buildResponseNode = (nodeId = 'n4', xPos = 900, yPos = 0, title = 'Slack AI Response'): FlowNodeConfig => ({
  id: nodeId,
  title,
  subtitle: 'Final reply to user',
  icon: <UserIcon size={18} color="hsl(var(--foreground))" />,
  xPosition: xPos,
  yPosition: yPos,
  dialogBuilder: (_item: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) => {
    const responseText =
      deriveAssistantText(md) ??
      pickFirstString(md, ['response_text', 'clean_text', 'text', 'assistant_response', 'answer']);

    const fields: DialogField[] = [];

    // If no response text, show informational message
    if (!responseText) {
      fields.push({
        label: 'Status',
        value: '⚠️ Not applicable',
        help: 'No AI response was generated for this message. This typically means the Slack bot was not mentioned or routed, so no agent response was produced.',
      });
      return fields;
    }

    if (responseText) {
      fields.push({
        label: 'Response Text',
        value: (
          <div
            className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
            title={responseText}
          >
            <ReactMarkdown>{responseText}</ReactMarkdown>
          </div>
        ),
        help: 'Response rendered as markdown',
      });
    }

    const statusCode = pickFirstNumberOrString(md, ['status_code', 'http_status', 'status']);
    if (statusCode !== undefined && statusCode !== null) {
      fields.push({
        label: 'Status Code',
        value: toDisplay(statusCode),
      });
    }

    return fields;
  },
});

/**
 * Helper: Build user interaction node (for warn/proceed flows)
 */
const buildUserInteractionNode = (xPos = 0, yPos = 200, subtitle = 'User response to compliance'): FlowNodeConfig => ({
  id: 'n4',
  title: 'Slack User Interaction',
  subtitle,
  icon: <MousePointerClick size={18} color="#8b5cf6" />,
  xPosition: xPos,
  yPosition: yPos,
  dialogBuilder: (_item: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) => {
    // Get user action type and decisions
    const userActionType = pickFirstString(md, ['user_action', 'user_action.value']);
    const promptDecision = pickFirstString(md, ['prompt_decision_received', 'prompt_decision_received.value']);
    const attachmentDecisions = (getByPath(md, 'attachment_decisions.value') ||
      getByPath(md, 'attachment_decisions')) as Record<string, string> | undefined;
    const attachmentResults = (getByPath(md, 'attachment_results.value') || getByPath(md, 'attachment_results')) as
      | AttachmentResult[]
      | undefined;

    const currentMessage = pickFirstString(md, ['slack_data.query', 'message', 'user_message_text']);
    const originalMessage = pickFirstString(md, ['original_user_message']);
    const suggestedAmendment = (md.filter_response as { value?: { amendment?: string } })?.value?.amendment;

    // Check if user made attachment decisions
    const hasAttachmentDecisions = attachmentDecisions && Object.keys(attachmentDecisions).length > 0;

    // Determine action type based on backend user_action value
    const isManualEdit = userActionType === 'edit_message';
    const isAttachmentOnly = userActionType === 'attachment_decisions';

    let userAction = 'Proceeded';
    let actionDetail = 'User chose to proceed';

    // Determine action type based on context (with attachment awareness)
    if (isManualEdit) {
      userAction = 'Manual Edit';
      actionDetail = hasAttachmentDecisions
        ? 'User manually edited message and made attachment decisions'
        : 'User manually edited the message after compliance warning';
    } else if (isAttachmentOnly) {
      userAction = 'Attachment Decisions Only';
      actionDetail = 'User made decisions on attachment compliance (message allowed)';
    } else if (promptDecision === 'accept_changes' || (suggestedAmendment && currentMessage === suggestedAmendment)) {
      userAction = 'Accepted Amendment';
      actionDetail = hasAttachmentDecisions
        ? 'User accepted message amendment and made attachment decisions'
        : 'User accepted the suggested safer message';
    } else if (promptDecision === 'override_submit' || currentMessage === originalMessage) {
      userAction = 'Proceeded With Original';
      actionDetail = hasAttachmentDecisions
        ? 'User proceeded with original message and made attachment decisions'
        : 'User chose to proceed with the original message';
    }

    const fields: DialogField[] = [];
    fields.push({
      label: 'User Action',
      value: toDisplay(userAction),
      help: actionDetail,
    });

    // Show the message only if there was a prompt-level decision or manual edit
    const shouldShowMessage = isManualEdit || (promptDecision && !isAttachmentOnly);
    if (shouldShowMessage && currentMessage) {
      const messageLabel = isManualEdit ? 'Edited Message' : 'Message Proceeding With';
      const messageHelp = isManualEdit
        ? 'The user manually edited this message to address compliance concerns'
        : 'The message that will be sent to the agent';

      fields.push({
        label: messageLabel,
        value: (
          <div
            className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
            title={currentMessage}
          >
            <ReactMarkdown>{currentMessage}</ReactMarkdown>
          </div>
        ),
        help: messageHelp,
      });
    }

    // Always show attachment decisions if attachments were involved (regardless of whether user made decisions)
    if (attachmentResults && attachmentResults.length > 0) {
      const amendedFileIds =
        ((getByPath(md, 'amended_file_ids.value') || getByPath(md, 'amended_file_ids')) as
          | (string | number)[]
          | undefined) || [];

      fields.push({
        label: 'Attachment Decisions',
        value: (
          <AttachmentList
            attachments={attachmentResults}
            decisions={attachmentDecisions}
            amendedFileIds={amendedFileIds}
          />
        ),
        help: hasAttachmentDecisions
          ? 'User decisions for each attachment'
          : 'Attachment compliance results (no user decisions yet)',
      });
    }

    return fields;
  },
});

/**
 * Helper: Build block message node
 */
const buildBlockMessageNode = (xPos = 800, yPos = 0): FlowNodeConfig => ({
  id: 'n3',
  title: 'Slack Compliance Message',
  subtitle: 'Message about block',
  icon: <XCircle size={18} />,
  xPosition: xPos,
  yPosition: yPos,
  dialogBuilder: (_item: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) => {
    const blockMessage = pickFirstString(md, ['early_amend_response', 'error_response.detail', 'detail', 'response']);

    const fields: DialogField[] = [];

    if (blockMessage) {
      fields.push({
        label: 'Block Message',
        value: (
          <div
            className="prose prose-sm dark:prose-invert max-w-md max-h-[250px] overflow-y-auto border rounded-md p-3 bg-gray-50 dark:bg-gray-800"
            title={blockMessage}
          >
            <ReactMarkdown>{blockMessage}</ReactMarkdown>
          </div>
        ),
        help: 'Message shown to user explaining why their message was blocked',
      });
    }

    const overallAction = pickFirstString(md, ['overall_action', 'overall_action_early']);
    if (overallAction) {
      fields.push({
        label: 'Final Action',
        value: <span className="font-semibold capitalize text-red-600">{overallAction.toLowerCase()}</span>,
        help: 'Final action taken: Block',
      });
    }

    fields.push({
      label: 'Processing Status',
      value: (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <div>❌ No routing performed</div>
          <div>❌ No agent execution</div>
          <div>❌ No AI response generated</div>
        </div>
      ),
      help: 'Block action prevents further processing',
    });

    return fields;
  },
});

/**
 * Build complete flow based on type
 */
const buildFlow = (config: FlowConfig): FlowDefinition => {
  const { type } = config;

  let nodes: FlowNodeConfig[] = [];
  let edges: EdgeConfig[] = [];

  if (type === 'basic') {
    // Basic: n1 → n2 → n3 → n4
    nodes = [
      buildUserMessageNode(0, 0),
      buildComplianceNode(300, 0),
      buildRoutingNode('n3', 600, 0),
      buildResponseNode('n4', 900, 0),
    ];
    edges = [
      {
        sourceId: 'n1',
        targetId: 'n2',
        colorResolver: (_: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) =>
          statusColor(getWorstAction(md) as string | undefined),
      },
      {
        sourceId: 'n2',
        targetId: 'n3',
        colorResolver: (_: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) =>
          unwrap<boolean>(md?.router_question_path) === true ? '#6366f1' : '#94a3b8',
      },
      {
        sourceId: 'n3',
        targetId: 'n4',
        colorResolver: (_: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) =>
          statusColor((md?.overall_action as { value?: string })?.value as string | undefined),
      },
    ];
  } else if (type === 'enhanced') {
    // Enhanced with tools: n1 → n2 → n3 → n4 (agent execution) → n5
    nodes = [
      buildUserMessageNode(0, 0),
      buildComplianceNode(250, 0),
      buildRoutingNode('n3', 500, 0),
      buildAgentExecutionNode('n4', 750, 0),
      buildResponseNode('n5', 1000, 0),
    ];
    edges = [
      {
        sourceId: 'n1',
        targetId: 'n2',
        colorResolver: (_: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) =>
          statusColor(getWorstAction(md) as string | undefined),
      },
      {
        sourceId: 'n2',
        targetId: 'n3',
        colorResolver: (_: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) =>
          unwrap<boolean>(md?.router_question_path) === true ? '#6366f1' : '#94a3b8',
      },
      {
        sourceId: 'n3',
        targetId: 'n4',
        colorResolver: () => '#f59e0b',
      },
      {
        sourceId: 'n4',
        targetId: 'n5',
        colorResolver: (_: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) =>
          statusColor((md?.overall_action as { value?: string })?.value as string | undefined),
      },
    ];
  } else if (type === 'block') {
    // Block: n1 → n2 (blocked) → n3
    nodes = [
      buildUserMessageNode(0, 0, 'Message blocked by compliance'),
      buildComplianceNode(400, 0, 'Message blocked', true),
      buildBlockMessageNode(800, 0),
    ];
    edges = [
      { sourceId: 'n1', targetId: 'n2', colorResolver: () => '#ef4444' },
      { sourceId: 'n2', targetId: 'n3', colorResolver: () => '#ef4444' },
    ];
  } else if (type === 'warn-proceed') {
    // Warn/Proceed: Row 1 (n1 → n2 → n3) + Row 2 (n4 → n5 → n6 → n7)
    nodes = [
      buildUserMessageNode(0, 0),
      buildComplianceNode(250, 0, 'Policy and safety checks'),
      buildWarningNode(500, 0),
      buildUserInteractionNode(0, 200),
      buildRoutingNode('n5', 250, 200),
      buildAgentExecutionNode('n6', 500, 200),
      buildResponseNode('n7', 750, 200),
    ];
    edges = [
      {
        sourceId: 'n1',
        targetId: 'n2',
        colorResolver: (_: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) => {
          const action = getWorstAction(md);
          if (action === 'AMEND') return '#f59e0b';
          if (action === 'WARN') return '#eab308';
          if (action === 'BLOCK') return '#ef4444';
          return '#10b981'; // Green for ALLOW
        },
      },
      { sourceId: 'n2', targetId: 'n3', colorResolver: () => '#f59e0b' },
      {
        sourceId: 'n2',
        targetId: 'n4',
        colorResolver: (_: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) => {
          const action =
            (md.filter_response as { value?: { action?: string } })?.value?.action ||
            pickFirstString(md, ['overall_action_early', 'overall_action', 'action']);
          if (action === 'AMEND') return '#f59e0b';
          if (action === 'WARN') return '#eab308';
          if (action === 'BLOCK') return '#ef4444';
          return '#10b981'; // Green for ALLOW
        },
      },
      { sourceId: 'n3', targetId: 'n4', colorResolver: () => '#8b5cf6' },
      {
        sourceId: 'n4',
        targetId: 'n2',
        colorResolver: (_: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) => {
          const action =
            (md.filter_response as { value?: { action?: string } })?.value?.action ||
            pickFirstString(md, ['overall_action_early', 'overall_action', 'action']);
          if (action === 'AMEND') return '#f59e0b';
          if (action === 'WARN') return '#eab308';
          if (action === 'BLOCK') return '#ef4444';
          return '#10b981'; // Green for ALLOW
        },
      },
      { sourceId: 'n4', targetId: 'n5', colorResolver: () => '#6366f1' },
      { sourceId: 'n5', targetId: 'n6', colorResolver: () => '#10b981' },
      {
        sourceId: 'n6',
        targetId: 'n7',
        colorResolver: (_: ObservabilityLogsRetrieve200ResultsItem, md: MetaDoc) =>
          statusColor((md?.overall_action as { value?: string })?.value as string | undefined),
      },
    ];
  }

  return { nodes, edges };
};

// Export pre-configured flows
export const slackFlowDefinition = buildFlow({ type: 'basic' });
export const slackFlowEnhanced = buildFlow({ type: 'enhanced' });
export const slackFlowBlock = buildFlow({ type: 'block' });
export const slackFlowWarnProceed = buildFlow({ type: 'warn-proceed' });
